import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { betCodeAPI } from '../api/endpoints';
import { 
  formatTimeAgo, formatXPUNT, getStatusColor, 
  getResultEmoji, getConfidenceBadge 
} from '../utils/helpers';
import { 
  FaArrowLeft, FaThumbsUp, FaComment, FaShare, 
  FaFlag, FaCheck, FaTimes, FaClock, FaUser,
  FaFire, FaTrophy, FaEye, FaLink, FaCopy,
  FaStar, FaChartLine, FaDollarSign
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CommentSection from '../components/bet-codes/CommentSection';
import MarkBetCode from '../components/bet-codes/MarkBetCode';
import './BetCodeDetail.css';

// ==================== HELPER: GET DISPLAY STATUS ====================
const getDisplayStatus = (betCode) => {
  if (!betCode) return 'running';
  
  const status = (betCode.status || '').toLowerCase();
  const markedResult = (betCode.marked_result || '').toLowerCase();
  const validationStatus = (betCode.validation_status || '').toLowerCase();
  const isMarked = betCode.is_marked === true || betCode.is_marked === 1;
  
  // Priority 1: If validation is approved AND bet is marked, use marked_result
  if (validationStatus === 'approved' && isMarked) {
    if (markedResult === 'won' || markedResult === 'win') return 'won';
    if (['loss', 'lost', 'loose', 'lose'].includes(markedResult)) return 'loose';
  }
  
  // Priority 2: If bet is marked (even without approval), use marked_result
  if (isMarked) {
    if (markedResult === 'won' || markedResult === 'win') return 'won';
    if (['loss', 'lost', 'loose', 'lose'].includes(markedResult)) return 'loose';
  }
  
  // Priority 3: Use status field
  if (status === 'won' || status === 'win') return 'won';
  if (['loss', 'lost', 'loose', 'lose'].includes(status)) return 'loose';
  if (status === 'completed') return 'completed';
  if (status === 'private') return 'private';
  
  // Priority 4: Default
  return status || 'running';
};

// ==================== HELPER: GET STATUS LABEL ====================
const getStatusLabel = (status) => {
  const labels = {
    running: 'Running',
    won: 'Won',
    loose: 'Lost',
    lost: 'Lost',
    pending: 'Pending',
    private: 'Private',
    completed: 'Completed',
    refunded: 'Refunded',
    cancelled: 'Cancelled',
  };
  return labels[status?.toLowerCase()] || status || 'Running';
};

// ==================== HELPER: GET STATUS ICON ====================
const getStatusIcon = (status) => {
  switch (status) {
    case 'won': return '✅';
    case 'loose': return '❌';
    case 'running': return '🔵';
    case 'pending': return '⏳';
    case 'completed': return '✓';
    default: return '●';
  }
};

const BetCodeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [betCode, setBetCode] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [showProofUpload, setShowProofUpload] = useState(false);
  const [proofImage, setProofImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ==================== FETCH BET CODE ====================
  const fetchBetCode = useCallback(async () => {
    try {
      const response = await betCodeAPI.getSingle(id);
      
      // Handle response structure
      let data = null;
      if (response.data?.data) {
        data = response.data.data;
      } else if (response.data) {
        data = response.data;
      }
      
      console.log('Bet code data:', data);
      setBetCode(data);
    } catch (error) {
      console.error('Failed to fetch bet code:', error);
      toast.error('Bet code not found');
      navigate('/bet-codes');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchBetCode();
  }, [fetchBetCode]);

  // ==================== HANDLE LIKE ====================
  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to like');
      return;
    }
    try {
      await betCodeAPI.like(id);
      setIsLiked(true);
      setBetCode(prev => ({
        ...prev,
        likes_count: (prev.likes_count || 0) + 1,
      }));
      toast.success('Liked!');
    } catch (error) {
      console.error('Failed to like:', error);
      toast.error('Failed to like');
    }
  };

  // ==================== HANDLE SHARE ====================
  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: betCode?.title || 'XPunters Bet Code',
        text: `Check out this bet code: ${betCode?.code}`,
        url: url,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  // ==================== HANDLE PROOF UPLOAD ====================
  const handleProofUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setProofImage(file);
    setShowProofUpload(true);
  };

  const submitProof = async () => {
    if (!proofImage) return;
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.readAsDataURL(proofImage);
      reader.onload = async () => {
        const base64 = reader.result;
        await betCodeAPI.uploadProof(id, { image_data: base64 });
        toast.success('Proof uploaded successfully! Waiting for validation.');
        setShowProofUpload(false);
        setProofImage(null);
        await fetchBetCode();
      };
    } catch (error) {
      console.error('Failed to upload proof:', error);
      toast.error('Failed to upload proof');
    } finally {
      setUploading(false);
    }
  };

  // ==================== LOADING ====================
  if (loading) {
    return (
      <div className="detail-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!betCode) {
    return (
      <div className="detail-not-found">
        <h2>Bet code not found</h2>
        <Link to="/bet-codes" className="btn btn-primary">
          <FaArrowLeft /> Back to Bet Codes
        </Link>
      </div>
    );
  }

  // ==================== COMPUTED VALUES ====================
  const displayStatus = getDisplayStatus(betCode);
  const statusColor = getStatusColor(displayStatus);
  const isCreator = betCode && user && String(betCode.author_id) === String(user.id);
  const canUploadProof = isCreator && displayStatus === 'running';

  // ==================== RENDER ====================
  return (
    <div className="bet-code-detail-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="detail-grid">
        {/* Left Column - Bet Code Info */}
        <div className="detail-main">
          <div className="detail-card">
            {/* Header */}
            <div className="detail-header">
              <div className="detail-meta">
                <span className="detail-platform">
                  {betCode.platform || 'General'}
                </span>
                
                {/* ==================== FIXED STATUS DISPLAY ==================== */}
                <span 
                  className={`detail-status status-${displayStatus}`}
                  style={{ color: statusColor }}
                >
                  {getStatusIcon(displayStatus)} {getStatusLabel(displayStatus)}
                </span>
                
                {betCode.is_private && (
                  <span className="detail-private">🔒 Private</span>
                )}
                {betCode.is_rollover && (
                  <span className="detail-rollover">🔄 Rollover</span>
                )}
              </div>
              <div className="detail-actions">
                <button 
                  onClick={handleLike} 
                  className={`action-btn ${isLiked ? 'liked' : ''}`}
                  title="Like"
                >
                  <FaThumbsUp /> {betCode.likes_count || 0}
                </button>
                <button onClick={handleShare} className="action-btn" title="Share">
                  <FaShare />
                </button>
              </div>
            </div>

            {/* Title and Content */}
            <h1 className="detail-title">{betCode.title}</h1>
            {betCode.content && (
              <p className="detail-content">{betCode.content}</p>
            )}

            {/* Bet Code */}
            <div className="detail-code-wrapper">
              <div className="detail-code">{betCode.code}</div>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(betCode.code);
                  toast.success('Code copied!');
                }}
                className="copy-code-btn"
                title="Copy code"
              >
                <FaCopy />
              </button>
            </div>

            {/* Stats */}
            <div className="detail-stats">
              <div className="stat-item">
                <span className="stat-value">{betCode.number_of_games || 0}</span>
                <span className="stat-label">Games</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-value">{betCode.odds || 0}</span>
                <span className="stat-label">Odds</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-value">{betCode.win_rate || 0}%</span>
                <span className="stat-label">Win Rate</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-value">
                  {betCode.marks_won || 0}W / {betCode.marks_loss || 0}L
                </span>
                <span className="stat-label">Marks</span>
              </div>
            </div>

            {/* Extras */}
            <div className="detail-extras">
              {betCode.confidence && (
                <div className="extra-item">
                  <span className="extra-label">Confidence</span>
                  <span 
                    className="extra-value"
                    style={{ color: getConfidenceBadge(betCode.confidence).color }}
                  >
                    {getConfidenceBadge(betCode.confidence).label}
                  </span>
                </div>
              )}
              {betCode.stake > 0 && (
                <div className="extra-item">
                  <span className="extra-label">Stake</span>
                  <span className="extra-value">{formatXPUNT(betCode.stake)}</span>
                </div>
              )}
              {betCode.potential_win > 0 && (
                <div className="extra-item">
                  <span className="extra-label">Potential Win</span>
                  <span className="extra-value text-gold">
                    {formatXPUNT(betCode.potential_win)}
                  </span>
                </div>
              )}
            </div>

            {/* Author */}
            <div className="detail-author">
              <img 
                src={betCode.author_avatar || '/default-avatar.png'} 
                alt={betCode.author}
                className="author-avatar"
                onError={(e) => { e.target.src = '/default-avatar.png'; }}
              />
              <div className="author-info">
                <span className="author-name">{betCode.author}</span>
                <span className="author-time">
                  Posted {formatTimeAgo(betCode.created_at)}
                </span>
              </div>
              {betCode.validation_status === 'approved' && (
                <span className="validation-badge approved">
                  <FaCheck /> Validated
                </span>
              )}
              {betCode.validation_status === 'pending' && betCode.proof_image && (
                <span className="validation-badge pending">
                  <FaClock /> Pending Validation
                </span>
              )}
            </div>

            {/* Proof Image */}
            {betCode.proof_image && (
              <div className="proof-section">
                <h4>📸 Proof Image</h4>
                <img 
                  src={betCode.proof_image} 
                  alt="Proof" 
                  className="proof-image"
                  onClick={() => window.open(betCode.proof_image, '_blank')}
                />
                {betCode.validation_feedback && (
                  <p className="validation-feedback">
                    {betCode.validation_feedback}
                  </p>
                )}
              </div>
            )}

            {/* Upload Proof (Creator only, when running) */}
            {canUploadProof && (
              <div className="upload-proof-section">
                <h4>Upload Proof of Bet</h4>
                <p className="upload-hint">
                  Upload a screenshot of your bet slip for validation
                </p>
                <label className="upload-btn">
                  <FaLink /> Choose Image
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleProofUpload}
                    hidden
                  />
                </label>

                {showProofUpload && proofImage && (
                  <div className="proof-preview">
                    <img 
                      src={URL.createObjectURL(proofImage)} 
                      alt="Preview" 
                    />
                    <div className="preview-actions">
                      <button 
                        onClick={() => {
                          setShowProofUpload(false);
                          setProofImage(null);
                        }}
                        className="btn btn-secondary btn-sm"
                        disabled={uploading}
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={submitProof}
                        className="btn btn-primary btn-sm"
                        disabled={uploading}
                      >
                        {uploading ? <LoadingSpinner size="small" /> : 'Submit Proof'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mark Bet Code (only when running) */}
            {isAuthenticated && displayStatus === 'running' && (
              <MarkBetCode 
                betCodeId={betCode.id} 
                onMarked={fetchBetCode}
              />
            )}
          </div>
        </div>

        {/* Right Column - Comments */}
        <div className="detail-sidebar">
          <div className="comments-card">
            <h3>
              <FaComment /> Comments ({betCode.comments_count || 0})
            </h3>
            <CommentSection betCodeId={betCode.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetCodeDetail;